import React from 'react';
import { AlertTriangle } from 'lucide-react';
import { Helmet } from 'react-helmet';

const RiskWarning = () => {
  return (
    <div className="min-h-screen bg-black text-white pt-32 pb-20">
      <Helmet>
        <link rel="canonical" href="https://www.flumberico.fun/risk-warning" />
      </Helmet>
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-12">
          <AlertTriangle className="w-16 h-16 text-red-500 mx-auto mb-6" />
          <h1 className="text-5xl font-black mb-4 text-red-500">
            Risk Warning
          </h1>
          <p className="text-silver/70">Important Information About Cryptocurrency Risk</p>
        </div>

        <div className="bg-red-950/30 border-2 border-red-500/60 rounded-lg p-8 mb-10">
          <h2 className="text-3xl font-bold text-red-400 mb-4 text-center">READ THIS CAREFULLY</h2>
          <p className="text-white text-lg text-center leading-relaxed">
            Investing in cryptocurrencies, including $FLUMBERICO, carries substantial financial risk. You could lose your entire investment. This page outlines critical risks you must understand before participating.
          </p>
        </div>

        <div className="prose prose-invert prose-red max-w-none">
          <section className="mb-10 bg-red-950/20 border border-red-500/40 rounded-lg p-8">
            <h2 className="text-2xl font-bold text-red-400 mb-4">1. Extreme Volatility Risk</h2>
            <p className="text-white leading-relaxed mb-4">
              Meme coins like $FLUMBERICO are subject to extreme price volatility:
            </p>
            <ul className="list-disc list-inside text-white space-y-2 ml-4">
              <li>Prices can fluctuate by 50% or more in a single day</li>
              <li>Value may drop to zero with little or no warning</li>
              <li>Market sentiment can change rapidly and unpredictably</li>
              <li>Pump and dump schemes are common in meme coin markets</li>
              <li>Historical performance does not indicate future results</li>
            </ul>
          </section>

          <section className="mb-10 bg-red-950/20 border border-red-500/40 rounded-lg p-8">
            <h2 className="text-2xl font-bold text-red-400 mb-4">2. Total Loss of Investment</h2>
            <p className="text-white leading-relaxed mb-4">
              <span className="font-bold text-red-400">You may lose 100% of your investment.</span> This is not a theoretical risk—many cryptocurrency projects have become worthless, leaving investors with complete losses. Key risks include:
            </p>
            <ul className="list-disc list-inside text-white space-y-2 ml-4">
              <li>Token value could drop to zero</li>
              <li>Markets may become illiquid, preventing you from selling</li>
              <li>Project abandonment or failure</li>
              <li>Smart contract vulnerabilities</li>
              <li>Exchange delistings</li>
            </ul>
          </section>

          <section className="mb-10 bg-red-950/20 border border-red-500/40 rounded-lg p-8">
            <h2 className="text-2xl font-bold text-red-400 mb-4">3. Experimental Nature</h2>
            <p className="text-white leading-relaxed mb-4">
              FLUMBERICO is explicitly described as a "social experiment." This means:
            </p>
            <ul className="list-disc list-inside text-white space-y-2 ml-4">
              <li>The project is untested and unproven</li>
              <li>No guarantee of success or sustainability</li>
              <li>Features and tokenomics may change</li>
              <li>The experiment may fail or be discontinued</li>
              <li>Future development is not guaranteed</li>
            </ul>
          </section>

          <section className="mb-10 bg-red-950/20 border border-red-500/40 rounded-lg p-8">
            <h2 className="text-2xl font-bold text-red-400 mb-4">4. No Guarantees or Promises</h2>
            <p className="text-white leading-relaxed mb-4">
              <span className="font-bold text-red-400">IMPORTANT:</span> FLUMBERICO makes NO GUARANTEES about:
            </p>
            <ul className="list-disc list-inside text-white space-y-2 ml-4">
              <li>Token value appreciation or stability</li>
              <li>Liquidity or ability to sell tokens</li>
              <li>Reward distribution amounts or timelines</li>
              <li>Platform functionality or uptime</li>
              <li>Achievement of roadmap milestones</li>
              <li>Return on investment of any kind</li>
            </ul>
          </section>

          <section className="mb-10 bg-red-950/20 border border-red-500/40 rounded-lg p-8">
            <h2 className="text-2xl font-bold text-red-400 mb-4">5. Regulatory Risk</h2>
            <p className="text-white leading-relaxed mb-4">
              Cryptocurrency regulations vary by jurisdiction and are constantly evolving:
            </p>
            <ul className="list-disc list-inside text-white space-y-2 ml-4">
              <li>Governments may ban or restrict cryptocurrency trading</li>
              <li>Tax implications may be unclear or unfavorable</li>
              <li>Legal status of tokens may change</li>
              <li>Compliance requirements may affect token utility</li>
              <li>You are responsible for understanding local regulations</li>
            </ul>
          </section>

          <section className="mb-10 bg-red-950/20 border border-red-500/40 rounded-lg p-8">
            <h2 className="text-2xl font-bold text-red-400 mb-4">6. Technical and Security Risks</h2>
            <p className="text-white leading-relaxed mb-4">
              Blockchain technology carries inherent technical risks:
            </p>
            <ul className="list-disc list-inside text-white space-y-2 ml-4">
              <li>Smart contract bugs or vulnerabilities</li>
              <li>Wallet hacking or private key theft</li>
              <li>Exchange hacks or failures</li>
              <li>Blockchain network issues</li>
              <li>Transaction irreversibility (no chargebacks)</li>
              <li>Phishing and scam attempts</li>
            </ul>
          </section>

          <section className="mb-10 bg-red-950/20 border border-red-500/40 rounded-lg p-8">
            <h2 className="text-2xl font-bold text-red-400 mb-4">7. Meme Coin Specific Risks</h2>
            <p className="text-white leading-relaxed mb-4">
              Meme coins have unique characteristics that increase risk:
            </p>
            <ul className="list-disc list-inside text-white space-y-2 ml-4">
              <li>Often lack fundamental utility or revenue models</li>
              <li>Heavily dependent on social media hype and trends</li>
              <li>Susceptible to celebrity or influencer manipulation</li>
              <li>High concentration of tokens in few wallets (whale risk)</li>
              <li>Pump and dump schemes are prevalent</li>
              <li>Market makers may be absent, causing liquidity issues</li>
            </ul>
          </section>

          <section className="mb-10 bg-red-950/20 border border-red-500/40 rounded-lg p-8">
            <h2 className="text-2xl font-bold text-red-400 mb-4">8. No Insurance or Protection</h2>
            <p className="text-white leading-relaxed mb-4">
              Unlike traditional financial products:
            </p>
            <ul className="list-disc list-inside text-white space-y-2 ml-4">
              <li>Cryptocurrency investments are NOT insured by government agencies</li>
              <li>No deposit insurance or investor protection schemes apply</li>
              <li>No recourse for lost, stolen, or misdirected tokens</li>
              <li>Customer support may be limited or unavailable</li>
              <li>Disputes cannot typically be resolved through traditional legal channels</li>
            </ul>
          </section>

          <section className="mb-10 bg-red-950/20 border border-red-500/40 rounded-lg p-8">
            <h2 className="text-2xl font-bold text-red-400 mb-4">9. Not Financial Advice</h2>
            <p className="text-white leading-relaxed mb-4">
              <span className="font-bold text-red-400">CRITICAL DISCLAIMER:</span>
            </p>
            <ul className="list-disc list-inside text-white space-y-2 ml-4">
              <li>Nothing on this website constitutes financial, investment, tax, or legal advice</li>
              <li>We are not financial advisors or investment professionals</li>
              <li>All content is for informational and entertainment purposes only</li>
              <li>You must conduct your own research (DYOR)</li>
              <li>Consult qualified professionals before investing</li>
              <li>Past performance is not indicative of future results</li>
            </ul>
          </section>

          <section className="mb-10 bg-red-950/20 border border-red-500/40 rounded-lg p-8">
            <h2 className="text-2xl font-bold text-red-400 mb-4">10. Who Should NOT Invest</h2>
            <p className="text-white leading-relaxed mb-4">
              You should NOT participate in FLUMBERICO if:
            </p>
            <ul className="list-disc list-inside text-white space-y-2 ml-4">
              <li>You cannot afford to lose your entire investment</li>
              <li>You need the money for essential expenses</li>
              <li>You are investing borrowed money</li>
              <li>You don't understand cryptocurrency technology</li>
              <li>You have low risk tolerance</li>
              <li>You are looking for guaranteed returns</li>
              <li>Cryptocurrency is illegal in your jurisdiction</li>
            </ul>
          </section>

          <section className="mb-10 bg-gradient-to-br from-red-950/40 to-orange-950/40 border-2 border-red-500/60 rounded-lg p-8">
            <h2 className="text-3xl font-bold text-red-400 mb-6 text-center">Final Warning</h2>
            <div className="space-y-4 text-white text-lg leading-relaxed">
              <p className="font-bold">
                $FLUMBERICO IS A HIGH-RISK, EXPERIMENTAL MEME COIN.
              </p>
              <p>
                By purchasing or participating, you acknowledge that:
              </p>
              <ul className="list-disc list-inside space-y-2 ml-4">
                <li>You may lose 100% of your investment</li>
                <li>No returns or profits are guaranteed</li>
                <li>This is an experimental project that may fail</li>
                <li>You have read and understood all risks outlined above</li>
                <li>You are investing at your own risk</li>
                <li>You accept full responsibility for any losses</li>
              </ul>
              <p className="font-bold text-center mt-6 text-xl text-red-400">
                ONLY INVEST WHAT YOU CAN AFFORD TO LOSE!
              </p>
            </div>
          </section>
        </div>

        <div className="mt-12 text-center bg-yellow-900/20 border border-yellow-500/40 rounded-lg p-6">
          <p className="text-white mb-4">
            <span className="font-bold text-yellow-400">Required Reading:</span> We strongly recommend that all potential investors read this Risk Warning in full before participating in FLUMBERICO.
          </p>
          <p className="text-silver/70 text-sm">
            Questions? Seek advice from qualified financial, legal, and tax professionals.
          </p>
        </div>
      </div>
    </div>
  );
};

export default RiskWarning;